/**
 * EasybillPushButton — 1-Click-Push eines Angebots (+ Kunde) nach Easybill.
 *
 * Rollen-Sichtbarkeit:
 *  - admin / sales_director / sales_manager / super_admin:  Button sichtbar
 *  - sales_rep / installer:                                  Button verborgen
 *
 * States: idle → loading → success (grüner Badge + Link) | error (roter Toast).
 * Bereits gepushte Angebote: Badge statt Button.
 */
import { useEffect, useState } from "react";
import { Loader2, ExternalLink, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ALLOWED_ROLES = new Set(["admin", "sales_director", "sales_manager", "super_admin"]);

export function EasybillPushButton({ offerId, compact = false, onPushed }) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ pushed: null }); // null = loading first time
  const [pushing, setPushing] = useState(false);

  const canPush =
    user &&
    (user.is_super_admin || ALLOWED_ROLES.has(user.role));

  useEffect(() => {
    let alive = true;
    if (!canPush || !offerId) {
      setStatus({ pushed: false });
      return;
    }
    api.get(`/api/easybill/offers/${offerId}/status`)
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => { if (alive) setStatus({ pushed: false }); });
    return () => { alive = false; };
  }, [offerId, canPush]);

  if (!canPush || !offerId) return null;

  const handlePush = async (e) => {
    e?.stopPropagation?.();
    if (pushing) return;
    setPushing(true);
    try {
      const { data } = await api.post(`/api/easybill/offers/${offerId}/push`);
      if (data.already_pushed) {
        toast.info("Angebot war bereits in Easybill vorhanden — Status aktualisiert.");
      } else {
        toast.success(`Angebot ist in Easybill angelegt (ID ${data.easybill_document_id}).`);
      }
      setStatus({
        pushed: true,
        easybill_document_id: data.easybill_document_id,
        pdf_url: data.pdf_url,
      });
      onPushed?.(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Push zu Easybill fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setPushing(false);
    }
  };

  if (status.pushed === null) {
    // erste Statusabfrage läuft noch
    return compact
      ? <Loader2 size={13} className="animate-spin text-zinc-500" />
      : <span className="text-zinc-500 text-[12px] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> …</span>;
  }

  if (status.pushed) {
    return (
      <a
        href={status.pdf_url || "#"}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        data-testid={`easybill-badge-${offerId}`}
        className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-widest ${
          compact
            ? "px-2 py-0.5 text-[10px]"
            : "px-2.5 py-1 text-[11px]"
        } bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 hover:bg-[#00E575]/25 transition`}
        title={`In Easybill (ID ${status.easybill_document_id}) — Klick öffnet PDF`}
      >
        <CheckCircle2 size={compact ? 10 : 12} />
        Easybill
        {status.pdf_url && <ExternalLink size={compact ? 9 : 11} />}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={handlePush}
      disabled={pushing}
      data-testid={`easybill-push-${offerId}`}
      title="Kunde + Angebot zu Easybill übertragen"
      className={`inline-flex items-center gap-1 rounded-md border transition disabled:opacity-50 ${
        compact
          ? "h-7 px-2 text-[11px]"
          : "h-8 px-3 text-[12px]"
      } bg-zinc-900 hover:bg-[#00E575]/10 hover:text-[#00E575] hover:border-[#00E575]/50 text-zinc-300 border-zinc-800`}
    >
      {pushing
        ? <Loader2 size={compact ? 11 : 13} className="animate-spin" />
        : <Send size={compact ? 11 : 13} />}
      Easybill
    </button>
  );
}

export default EasybillPushButton;
